import {action, observable, runInAction} from 'mobx';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import isUndefined from 'lodash/isUndefined';
import {InstrumentCategories, TrackActionType, TrackPlayStatus, TrackType, UploadType} from '../constants/enums';
import audioManager from '../utils/AudioManager';
import actionStore from './ActionStore';
import AudioTrackAction from './AudioTrackActionStore';
import {getImageByInstrument, guid} from '../utils/commonUtility';
import {calculateWave, getFirstSampleAboveThreshold, isMidi, prepareMetronomeGap, roundDurationToBpm} from '../utils/audioUtility';
import {fetchBpmFromS3} from '../utils/awsUtility';
import {getColorByInstrument} from '../themes/colors';
import {instruments} from '../constants/constants';
import {isIpad, isMobile} from "../utils/screenUtility";

class PlayerStore {
    @observable subDivision = 0.25;
    @observable gridDivision = 1;

    @action
    setGridDivision(division) {
        this.gridDivision = division;
    }

    fixByGridDivision(bars, reverse = false) {
        const denominator = reverse ? this.subDivision / this.gridDivision : this.gridDivision / this.subDivision;
        return bars ? bars / denominator : 0;
    }

    getObjectBarLength(object) {
        return this.fixByGridDivision(60 / (object.masterBpm || object.bpm), true);
    }

    /**
     * Reset the object by pausing and playing it
     * @param object   {Object} The object to update
     */
    pausePlay = (object) => {
        audioManager.pausePlay(object.playerID);
    };

    /**
     * Reset the object by stop and playing it
     * @param object   {Object} The object to update
     * @param startTime  {Number}    The time to start playing from
     * @param endTime    {Number}    The time to stop playing in
     */
    reset = (object, startTime = null, endTime = null) => {
        if (isNumber(startTime)) {
            audioManager.setSlice(object.playerID, startTime, endTime);
        } else {
            audioManager.unsetSlice(object.playerID);
        }
        audioManager.pausePlay(object.playerID);
    };

    /**
     * Init track object to set mandatory fields
     */
    initTrack = (track) => {
        track.id = track.id || track.trackId;
        track.playStatus = TrackPlayStatus.Stopped;
        track.isLoading = false;
        track.loaded = false;
        track.duration = track.duration || null;
        track.originalDuration = track.originalDuration || track.duration;
        track.currentTime = 0;
        track.progress = 0;
        track.lastModified = Date.now();
        track.url = track.url || '';
        track.peaks = track.peaks || [];
        track.volume = isNumber(track.volume) ? track.volume : 0.5;
        track.prevVolume = isNumber(track.prevVolume) ? track.prevVolume : 0.5;
        track.originalBPM = track.originalBPM || track.bpm;
        track.type = track.type || TrackType.Track;
        track.trackActionData = track.trackActionData || [];
        track.savedActionsCount = track.trackActionData.length;
        track.playerID = null;
        track.playerColor = getColorByInstrument(track.instruments);
        track.startTime = track.startTime || null;
        track.endTime = track.endTime || null;
        track.addedToLoopers = false;
        track.version = track.version || 1;
        track.copiedFrom = track.copiedFrom || null;
        track.saved = isUndefined(track.saved) ? true : track.saved;
        track.pitch = track.pitch || 0;
        track.tsBeatsPerBar = track.tsBeatsPerBar || 4;
        track.tsBeatsMeter = track.tsBeatsMeter || 4;
        track.downloadPct = track.downloadPct || 0;
        track.wave = track.wave || [];
        track.attachments = track.attachments || [];
        track.dragAction = false;
        track.sharedFolderId = track.sharedFolderId


        if (!isEmpty(track.instruments)) {
            track.instruments = track.instruments.filter(obj => !!obj).map((instrument) => {
                if (!instrument.category) {
                    const idx = instruments.findIndex(o => o.name === instrument.name || o.name === instrument);
                    if (idx !== -1) {
                        return instruments[idx];
                    }
                }
                return instrument;
            });
        }

        if (!track.backgroundImage || !track.backgroundImage.match(/[a-f0-9]{7}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/)) {
            track.backgroundImage = getImageByInstrument(track.instruments);
        }

        return track;
    };

    /**
     * initRotation tracks when received track array
     */
    prepareTracks(tracks) {
        return tracks.map((track) => {
            if (isNumber(track.order)) {
                return track;
            }
            track = this.initTrack(track);
            return track;
        });
    }


    /**
     * Set player to run in loop
     */
        // loop = (object, start, end) => {
    loop = () => {
        // audioManager.loop(object.playerID, start, end);
    };

    applyTrackActionDataToAudio = (object) => {
        let audioActions = this.prepareActions(object);
        audioManager.applyActionsDebounce(object.playerID, audioActions);
    };

    /**
     * generates audio action bar map for trackActions
     * the result is the a map from bar -> the FINAL audio actions that needs to be performed on this bar
     * mute and unmute of the same bar, cancels itself.
     */
    prepareActions = (object) => {
        let actions = actionStore.getByTrackId(object.trackId, true);
        let tsMeter = 4;
        let audioActions = [];
        actions.forEach(action => {
            switch (action.type) {
                case TrackActionType.ChangeTSMeter:
                    tsMeter = action.target[1];
                    break;
                default:
            }
            let beatLength = (60 / object.masterBpm) * (4 / tsMeter) / action.gridDivision;
            audioActions.push(new AudioTrackAction(action.type, action.source.slice(), action.target.slice(), action.splitIndex, beatLength));
        });
        return audioActions;
    };

    /**
     * Perform the play with subscribe if not subscribed
     * @param object {Object}    The track object
     * @param forceStatus   {Number} The play status to force to
     * @param fromTime
     * @param toTime
     * @param stopAll
     */
    togglesSubscribePlayPause = async (object, forceStatus, fromTime = null, toTime = null, stopAll = false, useMediaElement = false) => {
        if (isEmpty(object.playerID)) {
            await this.subscribe(object, false, true, useMediaElement);
        }
        this.togglePlayPause(object, forceStatus, fromTime || object.startTime, toTime || object.endTime, stopAll);
    };

    stopAll() {
        audioManager.stopAll();
    }

    pauseAll() {
        audioManager.pauseAll();
    }

    generateEvents = (object, runInLoop, subscribeToTimeUpdate, isApplyTrackActionDataToAudio = true) => {
        return {
            onDurationChange: this.handleDurationChange.bind(this, object),
            onLoaded: this.handleAudioLoaded.bind(this, object, isApplyTrackActionDataToAudio),
            onPlay: this.handlePlayerStatusAndTimeChanges.bind(this, object),
            onEnd: !runInLoop ? this.togglePlayPause.bind(this, object, TrackPlayStatus.Stopped) : null,
            onStopPause: this.handlePlayerStatusAndTimeChanges.bind(this, object),
            onTimeUpdate: subscribeToTimeUpdate ? this.handlePlayerStatusAndTimeChanges.bind(this, object) : null,
            onUpdateProgress: this.onUpdateProgress.bind(this, object)
        };
    };

    /**
     * Subscribe to audio player wrapper and set the player id into the object under playerID for future usage
     * Apply all the track action to the track audio
     * @param object {Object}    The object to subscribe
     * @param runInLoop {Boolean}   Indicator whether or not to play in loop
     * @param subscribeToTimeUpdate    {Boolean}    Specify whether or not you want to subscribe to the timeUpdate event
     */
    @action
    async subscribe(object, runInLoop = false, subscribeToTimeUpdate = false, useMediaElement = false) {
        // console.log('subscribed  called: ' + object.id + ', objectbpm: ' + object.bpm);
        object.loaded = false;
        object.subscribing = true;
        if (!useMediaElement) {
            await this.fetchBpmIfNeeded(object);
        }
        const events = this.generateEvents(object, runInLoop, subscribeToTimeUpdate, false);
        await runInAction(async () => {
            if (object.fileId.indexOf('rnmf') === -1) {
                object.fileId = object.fileId.replace(/_.*/g, ''); // legacy for old projects
            }
            // object.fileId = this.getFileIdWithBpm(object);
            if (object.copyData) {
                object.playerID = await audioManager.createAndSubscribeNewStem({
                    fileId: object.fileId,
                    fileType: object.fileType,
                    speed: object.bpm / object.originalBPM,
                    pitch: object.pitch,
                    bpm: object.bpm,
                    originalBPM: object.originalBPM
                }, object.copyData.playerID, object.copyData.startTime, object.copyData.endTime, events);
            } else {
                let isDrums = false;
                let isPiano = false;
                try {
                    isDrums = object.instruments.some(i => {
                        return i.category === InstrumentCategories.Drums || i.category === InstrumentCategories.Percussion
                    })
                    isPiano = object.instruments.some(i => {
                        return i.category === InstrumentCategories.PianoAndKeys || i.category === InstrumentCategories.Keyboard
                    })
                } catch (e) {
                }
                object.playerID = await audioManager.subscribe({
                    fileId: object.fileId,
                    fileType: object.fileType,
                    speed: object.bpm / object.originalBPM,
                    pitch: object.pitch,
                    bpm: object.bpm,
                    originalBPM: object.originalBPM,
                    ignorePitch: object.isMetronome || isDrums,
                    ignoreBPM: object.isMetronome,
                    useMonoVersion: (isMobile() || isIpad()) && !isPiano
                }, events, runInLoop, object.duration, useMediaElement);
            }
        });
        await runInAction(async () => {
            // console.log('@@@ subscribe change: before:  Duration', object.duration, ', orig: ', object.originalDuration );
            object.duration = roundDurationToBpm(object.bpm, object.duration);
            object.originalDuration = object.originalDuration || ((object.duration * object.bpm) / object.originalBPM);
            // console.log('@@@ subscribe change: after:  Duration', object.duration, ', orig: ', object.originalDuration);

            if (object.copyData) {
                object.loaded = true;
            }
            object.subscribing = false;
            if (!useMediaElement && !isMidi(object.fileType)) {
                //todo both loop and kind of do the same thing...
                const ignoreFirst = 0;//60 / object.originalBPM;
                object.latencyOffset = getFirstSampleAboveThreshold(this.getBuffer(object), 0.1, ignoreFirst);
                let startOffset = 0;
                if (object.uploadType === UploadType.Record && object.latencyFixed) { // recording
                    let minLatency = 60/object.originalBPM;//getMinLatency(object.originalBPM, object.tsBeatsPerBar, 200);
                    startOffset = object.latencyOffset % (60/object.originalBPM);
                    console.log('latency:', object.latencyOffset, 'ignoreFirst:', ignoreFirst, 'startOffset', startOffset, 'minLatency', minLatency);
                    audioManager.setLatencyOffset(object.playerID, startOffset)
                }
                object.wave = calculateWave(this.getBuffer(object), startOffset);
            } else {
                object.wave = [];
                object.latencyOffset = 0;
            }
        });
    }


    /**
     * Fetch s3 data and append to track
     */
    @action
    async fetchBpmIfNeeded(object) {
        if (!isEmpty(object) && (!isNumber(object.bpm) || object.bpm === -1)) {
            try {
                const bpmData = await fetchBpmFromS3(object.fileId);
                runInAction(() => {
                    object = {...object, bpm: bpmData.bpm, computedBpm: bpmData.bpm, originalBPM: bpmData.bpm};
                });
            } catch (err) {
                console.error(err);
            }
        }
    }


    /**
     * Set the initial track duration on track meta load
     * @param object   {Object} The object to update
     * @param player   {Object}    The player object
     * @param force
     */
    @action handleDurationChange = (object, player, force = false) => {
        if (!isEmpty(object) && !isEmpty(player)) {
            if (force || object.duration < player.duration) {
                // console.log('@@@ duration change: before: ', object.duration);
                object.duration = roundDurationToBpm(object.bpm, player.duration);
                // console.log('@@@ duration change: after: ', object.duration);
                // object.originalDuration = object.originalDuration || player.duration;
            }
            if (!isNumber(object.originalDuration)) {
                object.originalDuration = object.duration;
            }
            if (isNumber(player.currentTime) && player.currentTime >= 0) {
                object.currentTime = player.currentTime;
                object.progress = player.currentTime / player.duration;
            }
        }
    };

    /**
     * Handle Audio loaded event - triggered when the audio has finished downloading/loading
     * @param object   {Object} The object to update
     * @param player   {Object}    The player object
     */
    @action
    handleAudioLoaded(object, isApplyTrackActionDataToAudio, player) {
        object.loaded = true;
        if (!isNumber(object.originalDuration)) {
            // console.log('@@@ audio loaded change: before: original Duration', object.originalDuration);
            object.originalDuration = (player.duration * object.bpm) / object.originalBPM;
            // console.log('@@@ audio loaded change: after: original Duration', object.originalDuration);
        }
        if (isApplyTrackActionDataToAudio) {
            this.applyTrackActionDataToAudio(object);
        }
    }

    /**
     * Handle audio progress event
     * @param object   {Object} The object to update
     * @param player   {Object}    The player object
     */
    @action
    onUpdateProgress(object, pct) {
        object.downloadPct = pct;
    }

    /**
     * Perform redo for object action
     * @param tracks    {Array}  The sessionPlayerActions track objects
     */
    @action redo = (tracks) => {
        if (!isEmpty(tracks) && !actionStore.isEmpty()) {
            let redoAction = actionStore.getNextAction();
            if (redoAction && redoAction.type === TrackActionType.Split) {
                redoAction = actionStore.getNextAction();
            }
            return redoAction;
        }
    };

    /**
     * Perform Seek - update the progress to the desired location
     * @param tracks    {Array}  The sessionPlayerActions track objects
     */
    @action undo = (tracks) => {
        if (!isEmpty(tracks) && !actionStore.isEmpty()) {
            const undoAction = actionStore.removeLastAction();
            if (undoAction && undoAction.type === TrackActionType.Paste) {
                actionStore.removeLastAction();
            }
            return undoAction;
        }
    };

    /**
     * Perform Seek - update the progress to the desired location
     * @param object    {Object}  The object to update
     * @param progress   {Number} The new progress to set
     * @param duration  {Number}    Global duration seek according to
     */
    @action seekTo = (object, progress, duration) => {
        if (object) {
            audioManager.seekTo(object.playerID, progress);
            object.progress = progress;
            object.currentTime = progress * (duration || object.duration);
            object.lastModified = Date.now();
        }
    };

    /**
     * Perform Seek - update the progress to the desired location
     * @param objects    {Object}  The object to update
     * @param progress   {Number} The new progress to set
     * @param duration  {Number}    Global duration seek according to
     */
    @action seekToList = (objects, progress, duration) => {
        if (objects) {
            const playerIDs = objects.filter(object => object.playerID).map(object => object.playerID);
            audioManager.seekTo(playerIDs, progress);
            objects.forEach((object) => {
                object.progress = progress;
                object.currentTime = progress * (duration || object.duration);
                object.lastModified = Date.now();
            });
        }
    };

    /**
     * Update the track play data on time update
     * @param object   {Object}    The object to update
     * @param player   {Object}    The player object from the audio store
     */
    @action
    handlePlayerStatusAndTimeChanges(object, player) {
        if (object) {
            object.currentTime = (!player.currentTime || player.currentTime < 0) ? 0 : player.currentTime;
            object.lastModified = Date.now();
            const timeStatus = object.currentTime > 0 ? TrackPlayStatus.Paused : TrackPlayStatus.Stopped;
            const newPlayStatus = player.playing ? TrackPlayStatus.Playing : timeStatus;
            if (newPlayStatus !== object.playStatus) {
                // console.log('play status changed from: ' + object.playStatus + ', new: ' + newPlayStatus);
                object.playStatus = newPlayStatus;
            }
            if (object.playStatus !== TrackPlayStatus.Stopped) {
                const duration = player.paddingDuration || player.duration || object.duration;
                const startTimeDuration = isNumber(object.startTime) ? object.startTime / duration : 0;
                // object.duration = duration;
                object.progress = (!object.duration || (isNumber(object.endTime) && object.currentTime >= object.endTime)) ?
                    startTimeDuration : object.currentTime / duration;
            }
        }
    }

    /**
     * mark track as in favorite list or not;
     * @param object {Object}    The object to modify
     * @param forceStatus    {Number}   force a status on the track according to the TrackPlayStatus ENUM
     * @param fromTime  {Number}    The time to start playing from
     * @param toTime    {Number}    The time to stop playing in
     * @param stopAll
     */
    @action togglePlayPause = (object, forceStatus, fromTime = null, toTime = null, stopAll = false) => {
        if (object && object.playerID && object.bpm !== -1) {
            if (isNumber(forceStatus)) {
                object.playStatus = forceStatus;
                if (forceStatus === TrackPlayStatus.Stopped) {
                    object.progress = 0;
                    object.currentTime = 0;
                }
            } else {
                object.playStatus = (object.playStatus === TrackPlayStatus.Playing) ? TrackPlayStatus.Paused : TrackPlayStatus.Playing;
            }
            if (object.playStatus === TrackPlayStatus.Playing) {
                if (isNumber(toTime)) {
                    audioManager.setSlice(object.playerID, fromTime, toTime);
                } else {
                    audioManager.unsetSlice(object.playerID);
                }
                if (stopAll) {
                    this.pauseAll();
                }
                audioManager.play(object.playerID);
            } else if (object.playStatus === TrackPlayStatus.Paused) {
                audioManager.pause(object.playerID);
            } else {
                audioManager.stop(object.playerID);
            }
        }
    };

    @action togglePlayPauseList = (objects, forceStatus, fromTime = null, toTime = null, stopAll = false, immediate = false, delay = 0, recordCallback = null, suppressEvents = false, pauseAllCallback = null) => {
        if (objects) {
            objects.forEach((object) => {
                // object.playStatus = forceStatus;
                if (forceStatus === TrackPlayStatus.Stopped) {
                    object.progress = 0;
                    object.currentTime = 0;
                }

                if (isNumber(toTime)) {
                    audioManager.setSlice(object.playerID, fromTime, toTime);
                } else {
                    audioManager.unsetSlice(object.playerID);
                }
            });
            if (stopAll) {
                this.stopAll(suppressEvents);
            }

            const playerIDs = objects.filter(object => object.playerID).map(object => object.playerID);
            switch (forceStatus) {
                case TrackPlayStatus.Playing:
                    audioManager.play(playerIDs, immediate, delay, false, recordCallback, suppressEvents, this.pauseAllCallback);
                    break;
                case TrackPlayStatus.Paused:
                    audioManager.pause(playerIDs, suppressEvents);
                    break;
                default:
                    audioManager.stop(playerIDs, suppressEvents);
            }
        }
    };


    @action
    padTracks(objects, duration) {
        // console.log('callign pad tracks: ' + duration);
        const playerIds = objects
            .filter(object => !!object.playerID)
            .map(object => object.playerID);
        audioManager.padTracks(playerIds, duration);
    }

    @action
    changePlaybackRate(objects, playbackRate, playbackRateFromLast, pitch) {
        // console.log('callign pad tracks: ' + duration);
        const playerIds = objects
            .filter(object => !!object.playerID)
            .map(object => object.playerID);
        audioManager.changePlaybackRate(playerIds, playbackRate, playbackRateFromLast, pitch);
    }

    registerMetronome = async (bpm, duration, timeSignatureMeter, oldMetronomeTrack) => {
        let prevPlayStatus = oldMetronomeTrack && oldMetronomeTrack.playStatus;
        if (oldMetronomeTrack && oldMetronomeTrack.playerID) {
            audioManager.unsubscribe(oldMetronomeTrack.playerID);
        }
        let beatLength = 60 / bpm * (4 / timeSignatureMeter);
        let buffer = await prepareMetronomeGap();
        let metronomeTrack = this.initTrack({id: guid(), fileId: guid(), duration, bpm, isMetronome: true});
        let events = this.generateEvents(metronomeTrack, false, false);
        metronomeTrack.playerID = await audioManager.subscribe({
            fileId: metronomeTrack.fileId,
            buffer
        }, events, false, duration);


        const volume = oldMetronomeTrack ? oldMetronomeTrack.volume : 0;
        metronomeTrack.volume = volume;

        this.setVolume(metronomeTrack);


        audioManager.padTracks(metronomeTrack.playerID, duration);
        let actions = [];
        actions.push(new AudioTrackAction(TrackActionType.Copy, [], [0], 0, beatLength)); // copy the first bar
        for (let i = 0; i < duration / beatLength; i++) {
            actions.push(new AudioTrackAction(TrackActionType.Split, [], [i], i, beatLength));
            actions.push(new AudioTrackAction(TrackActionType.Paste, [0], [i], i, beatLength));
        }
        audioManager.applyActions(metronomeTrack.playerID, actions, prevPlayStatus === TrackPlayStatus.Playing);
        // console.log('new metoronom: beatLength: ' + beatLength + ', total length: ' + duration);
        return metronomeTrack;
    };

    /**
     * Update Track Volume
     * @param object {Object}    The object to modify
     * @param volume    {Number}   The volume to set to the track
     */
    @action changeVolume = (object, volume) => {
        if (!isEmpty(object) && isNumber(volume)) {
            object.prevVolume = object.volume;
            object.volume = volume;
            this.setVolume(object);
        }
    };

    /**
     * Update Track Volume
     */
    @action setVolume = (object) => {
        audioManager.volume(object.playerID, object.volume);
    };

    /**
     * Update Track Volume
     */
    @action changeMasterVolume = (objects, masterVolume) => {
        if (objects) {
            const playerIDs = objects.filter(object => object.playerID).map(object => object.playerID);
            audioManager.masterVolume(playerIDs, masterVolume);
        }
    };

    @action createNewStem = async (object, startTime, endTime) => {
        if (!isEmpty(object) && isNumber(startTime) && isNumber(endTime)) {
            const duration = endTime - startTime;
            const id = guid();
            const newObject = {
                id,
                duration,
                fileId: guid(),
                trackId: id,
                originalDuration: (object.originalDuration / object.duration) * duration,
                bpm: object.bpm, // important otherwise subscribe will try to download the bpm
                originalBPM: object.originalBPM,
                copyData: {
                    startTime: startTime * (object.bpm / object.originalBPM),
                    endTime: endTime * (object.bpm / object.originalBPM),
                    playerID: object.playerID
                }
            };
            this.initTrack(newObject);
            // await this.subscribe(newObject);
            return newObject;
        }
        return {};
    };


    /**
     * Clear the player - removing play status and clearing from the audio store
     */
    @action clearPlayer = (object) => {
        if (object && !isEmpty(object.playerID)) {
            this.togglePlayPause(object, TrackPlayStatus.Stopped);
            audioManager.unsubscribe(object.playerID);
            delete object.playerID;
        } else {
            audioManager.unsubscribeFileId(object.fileId);
        }
    };

    /**
     * Update the object with the up to date current time
     * @param object {Object}    The object apply to the s3 data
     */
    @action
    updateCurrentTime(object) {
        if (!isEmpty(object)) {
            object.currentTime = audioManager.getCurrentTime(object.playerID);
            object.progress = object.currentTime / object.duration;
            object.lastModified = Date.now();
        }
    }


    getBuffer = (object) => {
        if (!isEmpty(object)) {
            return audioManager.getBuffer(object.playerID);
        }
        return null;
    };

    getAudioBufferForTracks = async (tracks) => {
        if (tracks) {
            const playerIDs = tracks.filter(object => object.playerID).map(object => object.playerID);
            audioManager.stopAll();
            return await audioManager.play(playerIDs, true, 0, true);
        }
    };
}

export default PlayerStore;
